import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { formatGetal, isTelling } from '../format';
import { heeftEigenNaam, veldnaamVan } from '../veldnaam';
import { weeknummer } from '../week';
import { isOudeMeetmethode, metAantal, standVan, type Stand } from './Rapportkaart';
import { PLAKKENDE_KOP, PLAKKENDE_RIJKOP, ScrollableTable } from './ScrollableTable';
import type { AlleVeldenRij, GemetenWerksoort, Persoonsweek, Veldnamen, WerksoortSleutel } from '../work-quality.api';

export interface MedewerkerDetailsProps {
  week: Persoonsweek;
  /** De gekozen week ("2026-W43"); die staat in elk bijschrift. */
  weekKey: string;
}

/** Vastleggen, dan Controles; nooit samen in één tabel (M4). */
const WERKSOORTEN: ReadonlyArray<{ sleutel: WerksoortSleutel; naam: string }> = [
  { sleutel: 'myxxtract', naam: 'qa.eigen.werksoortMyxxtract' },
  { sleutel: 'pca', naam: 'qa.eigen.werksoortPca' },
];

const KOP4 = 'text-[0.9375rem] font-semibold text-[var(--foreground-heading)]';
const TEKST = 'text-[0.8125rem] text-[var(--foreground)]';
const BIJSCHRIFT = 'text-[0.75rem] text-[var(--foreground-muted)]';
const KOLOMKOP = 'h-auto py-2.5 text-[0.75rem] font-medium text-[var(--foreground-muted)]';
const CEL = 'px-3 py-2.5 align-top text-[0.8125rem] text-[var(--foreground)] tabular-nums';
const TITEL = 'text-[0.8125rem] font-semibold text-[var(--foreground-heading)]';

interface Rij {
  /** Een vaste rij zonder eigen sleutel valt terug op haar plek. */
  sleutel?: string;
  /** De eerste cel is de rijkop. */
  cellen: ReactNode[];
}

/** Eén tabel van Details; onder 768 px elke rij als `<dl>` met de kolomnamen als termen. */
function DetailTabel({ titel, kolommen, rijen }: { titel: string; kolommen: string[]; rijen: Rij[] }) {
  const smal = useIsMobile();

  if (smal) {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <p className={TITEL}>{titel}</p>
        <ul aria-label={titel} className="flex flex-col gap-2">
          {rijen.map((rij, rijplek) => (
            <li className="rounded-[8px] border border-[var(--border-soft)] bg-[var(--background)] p-3" key={rij.sleutel ?? rijplek}>
              <dl className="flex flex-col gap-1.5 text-[0.8125rem]">
                {rij.cellen.map((cel, plek) => (
                  <div className="flex min-w-0 flex-wrap justify-between gap-x-3" key={kolommen[plek]}>
                    <dt className="text-[var(--foreground-muted)]">{kolommen[plek]}</dt>
                    <dd className={`min-w-0 [overflow-wrap:anywhere] ${plek === 0 ? 'font-medium' : 'tabular-nums'}`}>{cel}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <ScrollableTable naam={titel}>
      <Table>
        <TableCaption className={`mt-0 mb-2 caption-top text-left ${TITEL}`}>{titel}</TableCaption>
        <TableHeader>
          <TableRow className="bg-[var(--background-muted)] hover:bg-[var(--background-muted)]">
            {kolommen.map((kolom, plek) => (
              <TableHead className={`${KOLOMKOP} ${plek === 0 ? PLAKKENDE_KOP : ''}`} key={kolom} scope="col">
                {kolom}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rijen.map(({ sleutel, cellen: [kop, ...cellen] }, rijplek) => (
            <TableRow className="group" key={sleutel ?? rijplek}>
              <TableHead className={`${CEL} ${PLAKKENDE_RIJKOP} h-auto font-medium [overflow-wrap:anywhere]`} scope="row">
                {kop}
              </TableHead>
              {cellen.map((cel, plek) => (
                <TableCell className={CEL} key={kolommen[plek + 1]}>
                  {cel}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ScrollableTable>
  );
}

function padenMetReden(velden: unknown[], reden: 'te_weinig_gegevens' | 'in_bespreking'): string[] {
  return velden.flatMap((v) => {
    const veld = v as { veld?: unknown; reden?: unknown } | null;
    return typeof veld === 'object' && veld !== null && veld.reden === reden && typeof veld.veld === 'string' ? [veld.veld] : [];
  });
}

function Werksoortdeel({ naam, rapport, veldnamen, weekKey }: { naam: string; rapport: GemetenWerksoort; veldnamen?: Veldnamen; weekKey: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const getal = (waarde: unknown) => (isTelling(waarde) ? formatGetal(waarde, locale) : t('qa.medewerker.nietGemeten'));
  const lijst = (namen: string[]) => new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(namen);
  const opties = { werksoort: naam, week: weeknummer(weekKey) };

  const nakijkers = (Array.isArray(rapport.perNakijker) ? rapport.perNakijker : []).filter((rij) => typeof rij === 'object' && rij !== null);
  const hoofdvelden: unknown[] = Array.isArray(rapport.hoofdsamenvatting?.velden) ? rapport.hoofdsamenvatting.velden : [];
  // Story 17: alle velden uit `alleVelden` als die lijst er is (ook leeg), anders de top vijf van hoofdsamenvatting
  // (een oudere dienst). Beide in dienstvolgorde: dqs ordent ze gelijk (contract §6), de browser sorteert niet.
  const velden: unknown[] = Array.isArray(rapport.alleVelden) ? rapport.alleVelden : hoofdvelden;
  const getoond = velden.filter((veld): veld is AlleVeldenRij => metAantal(veld));
  const teWeinig = padenMetReden(velden, 'te_weinig_gegevens').map((pad) => veldnaamVan(pad, veldnamen));
  // Velden in bespreking staan nooit in alleVelden; die komen altijd uit hoofdsamenvatting.
  const inBespreking = padenMetReden(hoofdvelden, 'in_bespreking').map((pad) => veldnaamVan(pad, veldnamen));
  /** "55% (22 van 40)" uit drie tellingen van de dienst; al het andere is niet gemeten (AD-19: geen rekensom). */
  const deel = (rij: (typeof nakijkers)[number]) =>
    rij.metCorrectieReden === undefined && isTelling(rij.aandeelMetCorrectie) && isTelling(rij.aantalMetCorrectie) && isTelling(rij.aantalProducten)
      ? t('qa.medewerker.deelWaarde', {
          aandeel: formatGetal(rij.aandeelMetCorrectie, locale),
          aantal: formatGetal(rij.aantalMetCorrectie, locale),
          producten: formatGetal(rij.aantalProducten, locale),
        })
      : t('qa.medewerker.nietGemeten');

  const productie = rapport.productie;
  let tempo: string;
  if (
    productie?.reden === undefined &&
    isTelling(productie?.perGewerkteDag) &&
    isTelling(productie.aantalProducten) &&
    isTelling(productie.aantalGewerkteDagen)
  ) {
    tempo = t('qa.medewerker.tempoWaarde', {
      perDag: formatGetal(productie.perGewerkteDag, locale),
      producten: t('qa.medewerker.producten', { count: productie.aantalProducten, aantal: formatGetal(productie.aantalProducten, locale) }),
      dagen: t('qa.medewerker.dagen', { count: productie.aantalGewerkteDagen, aantal: formatGetal(productie.aantalGewerkteDagen, locale) }),
    });
  } else if (productie?.reden === 'geen_gewerkte_dagen') {
    tempo = t('qa.eigen.productieGeenDagen');
  } else {
    tempo = t('qa.medewerker.nietGemeten');
  }

  return (
    <>
      {nakijkers.length === 0 ? (
        <p className={TEKST}>{t('qa.medewerker.geenNakijkers')}</p>
      ) : (
        <div className="flex min-w-0 flex-col gap-2">
          <DetailTabel
            kolommen={[t('qa.medewerker.kolomNakijker'), t('qa.medewerker.kolomProducten'), t('qa.medewerker.kolomOnderdelen'), t('qa.medewerker.kolomGemiddeld'), t('qa.medewerker.kolomDeel')]}
            rijen={nakijkers.map((rij, plek) => ({
              sleutel: `${rij.validator}-${plek}`,
              cellen: [rij.naam, getal(rij.aantalProducten), getal(rij.aantalBlokken), getal(rij.gemiddeldeBlokkenPerProduct), deel(rij)],
            }))}
            titel={t('qa.medewerker.perNakijkerTitel', opties)}
          />
          <p className={BIJSCHRIFT}>{t('qa.medewerker.nakijkersApart')}</p>
        </div>
      )}

      <div className="flex min-w-0 flex-col gap-2">
        {getoond.length === 0 ? (
          // Alleen als er echt niets is: een veld met te weinig gegevens of in bespreking wérd aangepast.
          teWeinig.length === 0 && inBespreking.length === 0 && <p className={TEKST}>{t('qa.medewerker.geenVelden')}</p>
        ) : (
          <DetailTabel
            kolommen={[
              t('qa.medewerker.kolomVeld'),
              t('qa.medewerker.kolomMetAanpassing'),
              t('qa.medewerker.kolomLeegAangevuld'),
              t('qa.medewerker.kolomWaardeGewijzigd'),
              t('qa.medewerker.kolomWeggehaald'),
            ]}
            rijen={getoond.map((veld) => ({
              sleutel: veld.veld,
              cellen: [
                heeftEigenNaam(veld.veld, veldnamen) ? veldnaamVan(veld.veld, veldnamen) : <span lang="en">{veld.veld}</span>,
                getal(veld.aantalProductenMetCorrectie),
                getal(veld.splitsing?.leegAangevuld),
                getal(veld.splitsing?.waardeGewijzigd),
                getal(veld.splitsing?.hallucinatie),
              ],
            }))}
            titel={t('qa.medewerker.veldenTitel', opties)}
          />
        )}
        {teWeinig.length > 0 && <p className={BIJSCHRIFT}>{t('qa.medewerker.teWeinigVelden', { velden: lijst(teWeinig) })}</p>}
      </div>

      <DetailTabel
        kolommen={[t('qa.medewerker.kolomOnderdeel'), t('qa.medewerker.kolomDezeWeek')]}
        rijen={[
          {
            cellen: [
              t('qa.medewerker.inBespreking'),
              inBespreking.length === 0
                ? t('qa.medewerker.inBesprekingGeen')
                : t('qa.medewerker.inBesprekingWaarde', { count: inBespreking.length, aantal: formatGetal(inBespreking.length, locale), velden: lijst(inBespreking) }),
            ],
          },
          { cellen: [t('qa.medewerker.tempo'), tempo] },
        ]}
        titel={t('qa.medewerker.nietMeeTitel', opties)}
      />
    </>
  );
}

/**
 * "Details voor de leidinggevende" (EXPERIENCE.md S2): per werksoort de nakijkers, de velden en wat niet meetelt
 * met het tempo, daarna één zin over zelf afgetekend werk. Elk getal komt klaar uit het antwoord (AD-19): hier
 * wordt niet geteld, gedeeld of gemiddeld, en er is geen totaalrij.
 */
export function MedewerkerDetails({ week, weekKey }: MedewerkerDetailsProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const werksoorten = typeof week.werksoorten === 'object' && week.werksoorten !== null ? week.werksoorten : null;
  const standen = WERKSOORTEN.map(({ sleutel, naam }) => ({ sleutel, naam: t(naam), stand: standVan<GemetenWerksoort>(werksoorten?.[sleutel]) }));

  // Dezelfde toets als het kader (Rapportkaart): anders zegt het kader "oudere meetmethode" en staan hier tabellen.
  if (isOudeMeetmethode(week, standen)) {
    return <p className={TEKST}>{t('qa.eigen.weekOudeMeetmethode')}</p>;
  }

  /** De zin voor een werksoort zonder uitsplitsing, gelezen uit dezelfde stand als het kader. */
  function zonderUitsplitsing(stand: Exclude<Stand, { soort: 'cijfer' }>, naam: string): string {
    switch (stand.soort) {
      case 'teWeinig':
        return stand.aantal === undefined
          ? t('qa.medewerker.teWeinigZonderAantal')
          : t('qa.medewerker.teWeinig', { aantal: formatGetal(stand.aantal, locale) });
      case 'geenWerk':
        return t('qa.medewerker.geenWerk', { werksoort: naam });
      case 'nietNagekeken':
        return t('qa.medewerker.nietNagekeken', { week: weeknummer(weekKey) });
      case 'oud':
        return t('qa.eigen.weekOudeMeetmethode');
      default:
        return t('qa.medewerker.geenUitsplitsing');
    }
  }

  // Zelf afgetekend bestaat alleen per persoon, over beide werksoorten samen: één zin, geen tabel (Design Notes).
  const dekking = typeof week.dekking === 'object' && week.dekking !== null && week.dekking.reden === undefined ? week.dekking : null;
  const zelf =
    dekking !== null && isTelling(dekking.aantalZelfGevalideerd) && isTelling(dekking.aantalBeoordeeld)
      ? { aantal: dekking.aantalZelfGevalideerd, totaal: dekking.aantalBeoordeeld }
      : null;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {standen.map(({ sleutel, naam, stand }) => (
        <section className="flex min-w-0 flex-col gap-4" data-testid={`werkkwaliteit-details-${sleutel}`} key={sleutel}>
          <h4 className={KOP4}>{naam}</h4>
          {stand.soort === 'cijfer' ? (
            <Werksoortdeel naam={naam} rapport={stand.rapport} veldnamen={week.veldnamen} weekKey={weekKey} />
          ) : (
            <p className={TEKST}>{zonderUitsplitsing(stand, naam)}</p>
          )}
        </section>
      ))}

      {zelf !== null && (
        <section className="flex min-w-0 flex-col gap-2">
          <h4 className={KOP4}>{t('qa.medewerker.zelfKop')}</h4>
          <p className={TEKST}>
            {zelf.aantal === 0
              ? t('qa.medewerker.zelfNiets')
              : t('qa.medewerker.zelfZin', { count: zelf.totaal, aantal: formatGetal(zelf.aantal, locale), totaal: formatGetal(zelf.totaal, locale) })}
          </p>
        </section>
      )}
    </div>
  );
}
